/**
 * Variant Images - Custom replacement for Easy Variant Images app
 * Uses product metafield for image-variant mapping (SEO-friendly)
 *
 * Supports both Dawn standard template (media-gallery) and
 * product-conversion template (.pc-gallery)
 *
 * Metafield: product.metafields.custom.variant_images
 * Format (JSON):
 * {
 *   "Zwart,Walnoot": [1, 2, 3],
 *   "Zwart,Witte eik": [4, 5, 6],
 *   "Wit,Walnoot": [7, 8, 9],
 *   "Wit,Witte eik": [10, 11, 12]
 * }
 *
 * Numbers = image positions in Shopify media gallery (1-based)
 */

if (!customElements.get('variant-images')) {
  customElements.define(
    'variant-images',
    class VariantImages extends HTMLElement {
      constructor() {
        super();
        this.mediaGallery = null;
        this.thumbnailSlider = null;
        this.allMediaItems = [];
        this.allThumbnailItems = [];
        this.imageMapping = {};
        this.currentOptions = {};
        this.sectionId = this.dataset.sectionId;
        this.isConversion = false;
      }

      connectedCallback() {
        this.initializeElements();
        this.parseImageMapping();
        this.subscribeToOptionChanges();

        // Initial filter based on current selection
        setTimeout(() => {
          this.updateCurrentOptions();
          this.filterImages();
        }, 100);
      }

      initializeElements() {
        // Try Dawn standard media gallery first
        const mediaGalleryEl = document.querySelector(`media-gallery#MediaGallery-${this.sectionId}`);

        if (mediaGalleryEl) {
          this.mediaGallery = mediaGalleryEl;
          this.thumbnailSlider = mediaGalleryEl.querySelector(`#Slider-Thumbnails-${this.sectionId}`);

          const galleryList = mediaGalleryEl.querySelector(`#Slider-Gallery-${this.sectionId}`);
          if (galleryList) {
            this.allMediaItems = Array.from(galleryList.querySelectorAll('li[data-media-id]'));
          }

          if (this.thumbnailSlider) {
            this.allThumbnailItems = Array.from(this.thumbnailSlider.querySelectorAll('li.thumbnail-list__item'));
          }
          this.isConversion = false;
        } else {
          // Fallback: Conversion template gallery
          const sectionEl = document.querySelector(`[data-section-id="${this.sectionId}"]`);
          if (!sectionEl) return;

          const galleryEl = sectionEl.querySelector('.pc-gallery');
          if (!galleryEl) return;

          this.mediaGallery = galleryEl;
          this.allMediaItems = Array.from(galleryEl.querySelectorAll('.pc-gallery__slide[data-media-id]'));
          this.allThumbnailItems = Array.from(galleryEl.querySelectorAll('.pc-gallery__thumb'));
          this.isConversion = true;
        }
      }

      parseImageMapping() {
        const mappingData = this.dataset.imageMapping;
        if (!mappingData) return;

        try {
          this.imageMapping = JSON.parse(mappingData);
        } catch (e) {
          console.error('Variant Images: Failed to parse image mapping', e);
        }
      }

      subscribeToOptionChanges() {
        // Subscribe to Shopify's pub/sub for option changes
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
            this.handleOptionChange();
          });

          subscribe(PUB_SUB_EVENTS.variantChange, () => {
            // Re-initialize after variant DOM updates
            setTimeout(() => {
              this.initializeElements();
              this.updateCurrentOptions();
              this.filterImages();
            }, 150);
          });
        }

        // Listen to Dawn variant selects
        document.querySelectorAll('variant-selects select, variant-selects input[type="radio"]').forEach(el => {
          el.addEventListener('change', () => {
            setTimeout(() => {
              this.updateCurrentOptions();
              this.filterImages();
            }, 50);
          });
        });

        // Listen to conversion template variant buttons
        const sectionEl = document.querySelector(`[data-section-id="${this.sectionId}"]`);
        if (sectionEl) {
          sectionEl.querySelectorAll('.pc-variant-btn, .pc-variant-swatch').forEach(el => {
            el.addEventListener('click', () => {
              setTimeout(() => {
                this.updateCurrentOptions();
                this.filterImages();
              }, 50);
            });
          });
        }
      }

      handleOptionChange() {
        setTimeout(() => {
          this.updateCurrentOptions();
          this.filterImages();
        }, 50);
      }

      updateCurrentOptions() {
        this.currentOptions = [];

        if (this.isConversion) {
          // Conversion template: read from active variant buttons/swatches
          const sectionEl = document.querySelector(`[data-section-id="${this.sectionId}"]`);
          if (!sectionEl) return;

          sectionEl.querySelectorAll('.pc-variant-btn--active, .pc-variant-swatch--active').forEach(btn => {
            if (btn.dataset.optionValue) {
              this.currentOptions.push(this.normalizeValue(btn.dataset.optionValue));
            }
          });
        } else {
          // Dawn standard: get options from variant-selects component
          const variantSelects = document.querySelector(`product-info[data-section="${this.sectionId}"] variant-selects`);
          if (!variantSelects) return;

          // Handle dropdowns
          variantSelects.querySelectorAll('select').forEach(select => {
            if (select.value) {
              const selectedOption = select.options[select.selectedIndex];
              const value = selectedOption?.dataset?.optionValue || select.value;
              this.currentOptions.push(this.normalizeValue(value));
            }
          });

          // Handle radio buttons / swatches
          variantSelects.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
            if (radio.value) {
              this.currentOptions.push(this.normalizeValue(radio.value));
            }
          });

          // Handle button/pill style variants
          variantSelects.querySelectorAll('[data-option-value].selected, [data-option-value][aria-checked="true"]').forEach(btn => {
            const optionValue = btn.dataset.optionValue;
            if (optionValue) {
              this.currentOptions.push(this.normalizeValue(optionValue));
            }
          });
        }
      }

      normalizeValue(value) {
        return value.toLowerCase().trim();
      }

      findMatchingKey() {
        // Find the mapping key that best matches current options
        // Uses exact matching to avoid "Eiken" matching "Witte Eiken"
        let bestKey = null;
        let bestScore = 0;

        for (const key of Object.keys(this.imageMapping)) {
          const keyParts = key.split(',').map(p => this.normalizeValue(p));

          // Check if all key parts exactly match a current option
          const allMatch = keyParts.every(part =>
            this.currentOptions.some(opt => opt === part)
          );

          if (allMatch && keyParts.length > bestScore) {
            bestScore = keyParts.length;
            bestKey = key;
          }
        }

        return bestKey;
      }

      filterImages() {
        if (!this.mediaGallery || this.allMediaItems.length === 0) return;
        if (Object.keys(this.imageMapping).length === 0) return;

        const matchingKey = this.findMatchingKey();
        const visiblePositions = matchingKey ? this.imageMapping[matchingKey] : [];

        // If no matching key found, show all images
        if (!matchingKey || !visiblePositions || visiblePositions.length === 0) {
          this.showAllImages();
          return;
        }

        const visibleMediaIds = [];
        let firstVisibleMediaId = null;
        let firstVisibleIndex = null;

        // Filter media items by position
        this.allMediaItems.forEach((item, index) => {
          const position = index + 1; // 1-based position
          const mediaId = item.dataset.mediaId;
          const shouldShow = visiblePositions.includes(position);

          if (shouldShow) {
            visibleMediaIds.push(mediaId);
            if (!firstVisibleMediaId) {
              firstVisibleMediaId = mediaId;
              firstVisibleIndex = index;
            }
          }

          // Update visibility
          item.classList.toggle('variant-images--hidden', !shouldShow);
          item.style.display = shouldShow ? '' : 'none';
        });

        // Update thumbnails visibility
        this.allThumbnailItems.forEach((thumb, index) => {
          const position = index + 1;
          const shouldShow = visiblePositions.includes(position);
          thumb.classList.toggle('variant-images--hidden', !shouldShow);
          thumb.style.display = shouldShow ? '' : 'none';
        });

        if (this.isConversion) {
          // Conversion template: activate first visible slide
          this.allMediaItems.forEach(item => item.classList.remove('pc-gallery__slide--active'));
          this.allThumbnailItems.forEach(thumb => thumb.classList.remove('pc-gallery__thumb--active'));

          if (firstVisibleIndex !== null) {
            this.allMediaItems[firstVisibleIndex].classList.add('pc-gallery__slide--active');
            if (this.allThumbnailItems[firstVisibleIndex]) {
              this.allThumbnailItems[firstVisibleIndex].classList.add('pc-gallery__thumb--active');
            }
          }

          // Update conversion gallery counter
          const counterCurrent = document.getElementById('pc-gallery-current');
          const counterTotal = document.getElementById('pc-gallery-total');
          if (counterCurrent) counterCurrent.textContent = '1';
          if (counterTotal) counterTotal.textContent = visiblePositions.length;
        } else {
          // Dawn standard template
          if (firstVisibleMediaId && this.mediaGallery.setActiveMedia) {
            this.mediaGallery.setActiveMedia(firstVisibleMediaId, false);
          }
          this.updateSliderCounter(visibleMediaIds.length);
          this.resetSliderPosition();
        }
      }

      showAllImages() {
        this.allMediaItems.forEach(item => {
          item.classList.remove('variant-images--hidden');
          item.style.display = '';
        });

        this.allThumbnailItems.forEach(thumb => {
          thumb.classList.remove('variant-images--hidden');
          thumb.style.display = '';
        });

        if (this.isConversion) {
          const counterTotal = document.getElementById('pc-gallery-total');
          if (counterTotal) counterTotal.textContent = this.allMediaItems.length;
        } else {
          this.updateSliderCounter(this.allMediaItems.length);
        }
      }

      updateSliderCounter(visibleCount) {
        const counter = this.mediaGallery?.querySelector('.slider-counter--total');
        if (counter) {
          counter.textContent = visibleCount;
        }
      }

      resetSliderPosition() {
        const slider = this.mediaGallery?.querySelector('.slider');
        if (slider) {
          slider.scrollTo({ left: 0, behavior: 'instant' });
        }
      }
    }
  );
}
